import { IoConfigActionEnum, IoConfigActions } from "../actions/ioConfigActions";
import { ioConfigInitialState } from "./initialState";

const ioConfigReducer = (
  state: State.IoConfig = ioConfigInitialState,
  action: IoConfigActions
): State.IoConfig => {
  switch (action.type) {
    case IoConfigActionEnum.SET_SELECTED_STANDARD_BOARD_ID: {
      const standardBoardId: string = action.payload;
      return {
        ...state,
        selectedStandardBoard: { ...state.selectedStandardBoard, standardBoardId }
      };
    }
    case IoConfigActionEnum.SET_SELECTED_STANDARD_NODE_ID: {
      const standardNodeId: string = action.payload;
      return {
        ...state,
        selectedStandardBoard: { ...state.selectedStandardBoard, standardNodeId }
      };
    }
    case IoConfigActionEnum.CLEAR_SELECTED_IO_IDS:
      return {
        ...state,
        selectedIoBoard: {
          ioBoardId: undefined,
          ioNodeId: undefined,
          ioPointId: undefined,
          ioActionId: undefined
        }
      };
    case IoConfigActionEnum.SET_SELECTED_STANDARD_POINT_ID: {
      const standardPointId: string | undefined = action.payload;
      return {
        ...state,
        selectedStandardBoard: { ...state.selectedStandardBoard, standardPointId }
      };
    }
    case IoConfigActionEnum.SET_SELECTED_STANDARD_ACTION_ID: {
      const standardActionId: string | undefined = action.payload;
      return {
        ...state,
        selectedStandardBoard: { ...state.selectedStandardBoard, standardActionId }
      };
    }
    case IoConfigActionEnum.SET_SELECTED_IO_BOARD_ID: {
      const ioBoardId: string = action.payload;
      return {
        ...state,
        selectedIoBoard: { ...state.selectedIoBoard, ioBoardId }
      };
    }
    case IoConfigActionEnum.SET_SELECTED_IO_NODE_ID: {
      const ioNodeId: string = action.payload;
      return {
        ...state,
        selectedIoBoard: { ...state.selectedIoBoard, ioNodeId }
      };
    }
    case IoConfigActionEnum.SET_SELECTED_IO_POINT_ID: {
      const ioPointId: string = action.payload;
      return {
        ...state,
        selectedIoBoard: { ...state.selectedIoBoard, ioPointId }
      };
    }
    case IoConfigActionEnum.SET_SELECTED_IO_ACTION_ID: {
      const ioActionId: string = action.payload;
      return {
        ...state,
        selectedIoBoard: { ...state.selectedIoBoard, ioActionId }
      };
    }
    case IoConfigActionEnum.SET_SELECTED_IO_CONFIGURATION_ID: {
      const ioConfigId: string = action.payload;
      return {
        ...state,
        selectedIoConfiguration: ioConfigId
      };
    }
    case IoConfigActionEnum.SET_IO_CONFIGURATIONS: {
      const ioConfigs: Api.IoConfiguration[] = action.payload;
      return {
        ...state,
        ioConfigurations: ioConfigs
      };
    }
    case IoConfigActionEnum.SET_OPENED_ROWS: {
      const openedRows: State.OpenedRows = action.payload;
      return {
        ...state,
        openedRows: openedRows
      };
    }
    case IoConfigActionEnum.UPDATE_STANDARD_POINTS: {
      // / Get all poinst from standard board and concat with previous points. Freshly loaded points will override duplicates in state
      const board: Api.StandardBoard = action.payload;

      let newPoints: Api.StandardPoint[] = [];
      board.standardNodes?.forEach(node => {
        newPoints = [...newPoints, ...node.standardPoints];
      });

      const updatedStandardBoards = [
        ...state.standardBoards.filter(p => p.id !== board.id), 
        board
      ];

      const newPointIds = new Set(newPoints.map(newPoint => newPoint.id));
      const updatedStandardPoints = [
        ...newPoints,
        ...state.standardPoints.filter(p => !newPointIds.has(p.id))
      ];
      return { 
        ...state, 
        standardBoards: updatedStandardBoards,
        standardPoints: updatedStandardPoints };
    }
    case IoConfigActionEnum.UPDATE_IO_NODE_POINTS: {
      // / Get all poinst from io board and concat with previous points.
      // / Freshly loaded points will override duplicates in state
      const ionodePoints: Api.IoNodePoint[] = action.payload;
      const newIoPointIds = new Set(ionodePoints.map(newPoint => newPoint.id));
      const updatedIoPoints = [
        ...ionodePoints,
        ...state.ionodePoints.filter(p => !newIoPointIds.has(p.id))
      ];
      return { ...state, ionodePoints: updatedIoPoints };
    }
    case IoConfigActionEnum.SET_IO_BOARDS:
      return { ...state, ioBoards: action.payload ?? [] };
    case IoConfigActionEnum.SET_IO_BOARDS_GROUPED:
      return { ...state, ioBoardsGrouped: action.payload ?? [] };
    case IoConfigActionEnum.SET_STANDARD_BOARDS: {
      const standardBoards = action.payload;
      return {
        ...state,
        standardBoards
      };
    }
    case IoConfigActionEnum.SET_STANDARD_POINTS: {
      const standardPoints = action.payload;
      return {
        ...state,
        standardPoints
      };
    }
    case IoConfigActionEnum.SET_IO_NODE_POINTS: {
      const ionodePoints = action.payload;
      return {
        ...state,
        ionodePoints
      };
    }
    case IoConfigActionEnum.UPDATE_OPENED_BOARD_TYPES: {
      const openedBoardTypeId = action.payload;
      const standardBoardTypes = state.openedRows.standardBoardTypes.includes(openedBoardTypeId)
        ? state.openedRows.standardBoardTypes.filter(item => item !== openedBoardTypeId)
        : [...state.openedRows.standardBoardTypes, openedBoardTypeId];
      return {
        ...state,
        openedRows: { ...state.openedRows, standardBoardTypes }
      };
    }
    case IoConfigActionEnum.UPDATE_OPENED_IO_BOARD_TYPES: {
      const openedIoBoardTypeId = action.payload;
      const ioBoardTypes = state.openedRows.ioBoardTypes.includes(openedIoBoardTypeId)
        ? state.openedRows.ioBoardTypes.filter(item => item !== openedIoBoardTypeId)
        : [...state.openedRows.ioBoardTypes, openedIoBoardTypeId];
      return {
        ...state,
        openedRows: { ...state.openedRows, ioBoardTypes }
      };
    }
    case IoConfigActionEnum.UPDATE_OPENED_IO_BOARDS: {
      const openedIoBoardId = action.payload;
      const ioBoards = state.openedRows.ioBoards.includes(openedIoBoardId)
        ? state.openedRows.ioBoards.filter(item => item !== openedIoBoardId)
        : [...state.openedRows.ioBoards, openedIoBoardId];
      return {
        ...state,
        openedRows: { ...state.openedRows, ioBoards }
      };
    }
    case IoConfigActionEnum.UPDATE_OPENED_IO_REVISIONS: {
      const openedIoRevisionId = action.payload;
      const ioRevisions = state.openedRows.ioRevisions.includes(openedIoRevisionId)
        ? state.openedRows.ioRevisions.filter(item => item !== openedIoRevisionId)
        : [...state.openedRows.ioRevisions, openedIoRevisionId];
      return {
        ...state,
        openedRows: { ...state.openedRows, ioRevisions }
      };
    }
    case IoConfigActionEnum.UPDATE_OPENED_STANDARD_BOARDS: {
      const openedRevisionId = action.payload;
      const revisions = state.openedRows.standardBoards.includes(openedRevisionId)
        ? state.openedRows.standardBoards.filter(item => item !== openedRevisionId)
        : [...state.openedRows.standardBoards, openedRevisionId];
      return {
        ...state,
        openedRows: { ...state.openedRows, standardBoards: revisions }
      };
    }
    case IoConfigActionEnum.UPDATE_IO_CONFIG_FILE_TEXT: {
      const ioConfigFileText = action.payload;
      return { ...state, ioConfigFileText };
    }

    case IoConfigActionEnum.SET_LOCAL_FETCHING_STATUS: {
      const localFetchingStatus = action.payload;
      return { ...state, localFetchingStatus };
    }
    default:
      return state;
  }
};

export default ioConfigReducer;
